import React, { useState, useEffect } from "react";
import axios from "axios";
import { useBeforeUnload } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { AlertTriangle, PlusCircle, Save, Search, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import SelectField from "../components/ui/SelectField";

const API_URL = process.env.REACT_APP_API_URL;

const NUEVO_OPTION = "__NUEVO__";

const normalizar = (valor = "") =>
  String(valor).normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toUpperCase();

const calcularEdadDesdeFecha = (fechaNacimiento) => {
  if (!fechaNacimiento) return { edad: "", med_edad: "" };

  const nacimiento = new Date(`${fechaNacimiento}T00:00:00`);
  if (Number.isNaN(nacimiento.getTime())) return { edad: "", med_edad: "" };

  const hoy = new Date();
  const diffMs = hoy.getTime() - nacimiento.getTime();
  if (diffMs < 0) return { edad: "", med_edad: "" };

  const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDias < 30) return { edad: String(diffDias), med_edad: "D" };
  if (diffDias < 365)
    return { edad: String(Math.floor(diffDias / 30)), med_edad: "M" };

  return { edad: String(Math.floor(diffDias / 365)), med_edad: "A" };
};

export default function PatientsPage() {
  // ── Catálogos ──────────────────────────────────────────────────────────────
  const [sedes, setSedes] = useState([]);
  const [tiposDoc, setTiposDoc] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [examenes, setExamenes] = useState([]);
  const [especies, setEspecies] = useState([]);
  const [razas, setRazas] = useState([]);
  const [perfilesExamen, setPerfilesExamen] = useState([]);

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("registrar");

  // ── Registrar tab ──────────────────────────────────────────────────────────
  const [selectedPerfil, setSelectedPerfil] = useState("");
  const [selectedSede, setSelectedSede] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProp, setloadingProp] = useState(false);
  const [nuevoConsecutivo, setNuevoConsecutivo] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState("");
  const [ownerExists, setOwnerExists] = useState(false);
  const [propietarios, setPropietarios] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState("");
  const [showOwnerForm, setShowOwnerForm] = useState(false);
  const [showPetForm, setShowPetForm] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const hasDraftOrder = showForm && Boolean(nuevoConsecutivo);

  const [petForm, setPetForm] = useState({
    nombre: "",
    nacio: "",
    genero: "",
    tipo_especie: "",
    tipo_raza: "",
    edad: "",
    med_edad: "",
    caracteristicas: "",
    propietario: "",
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [form, setForm] = useState({
    tipodcto_cod: "",
    nit: "",
    nom1: "",
    ape1: "",
    direccion: "",
    telefono: "",
    celular: "",
    email: "",
    ciudad_cod: "",
    zona: "",
    clte_codigo: "",
    medico_cod: "",
    examenes: [],
  });

  // ── Editar tab ─────────────────────────────────────────────────────────────
  const todayStr = new Date().toISOString().split("T")[0];
  const [editFechaDesde, setEditFechaDesde] = useState(todayStr);
  const [editFechaHasta, setEditFechaHasta] = useState(todayStr);
  const [ordenes, setOrdenes] = useState([]);
  const [loadingOrdenes, setLoadingOrdenes] = useState(false);
  const [selectedOrdenCod, setSelectedOrdenCod] = useState("");
  const [ordenDetalle, setOrdenDetalle] = useState(null);
  const [loadingOrdenDetalle, setLoadingOrdenDetalle] = useState(false);
  const [editForm, setEditForm] = useState({ medico_cod: "", clte_codigo: "", propietario_nit: "" });
  const [editExamenes, setEditExamenes] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editNuevosExamenes, setEditNuevosExamenes] = useState([]);
  const [editSelectedPerfil, setEditSelectedPerfil] = useState("");
  const [confirmDeleteExam, setConfirmDeleteExam] = useState(null);
  const [editPacientes, setEditPacientes] = useState([]);
  const [editPacienteSeleccionado, setEditPacienteSeleccionado] = useState("");
  const [editShowPetForm, setEditShowPetForm] = useState(false);
  const [editPetForm, setEditPetForm] = useState({
    nombre: "", nacio: "", genero: "", tipo_especie: "", tipo_raza: "",
    edad: "", med_edad: "", caracteristicas: "", propietario: "",
  });

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          sedesRes,
          tiposRes,
          ciudadesRes,
          clientesRes,
          medicosRes,
          examenesRes,
          especiesRes,
          razasRes,
          perfilesRes,
          propietariosRes,
        ] = await Promise.all([
          axios.get(`${API_URL}/util/sedes`, {
            params: { usuario: user.usuario },
          }),
          axios.get(`${API_URL}/util/tipo_dcto`),
          axios.get(`${API_URL}/util/ciudades`),
          axios.get(`${API_URL}/util/clientes`),
          axios.get(`${API_URL}/util/medicos`),
          axios.get(`${API_URL}/util/examenes`),
          axios.get(`${API_URL}/util/especies`),
          axios.get(`${API_URL}/util/razas`),
          axios.get(`${API_URL}/util/perfiles_examen`),
          axios.get(`${API_URL}/propietarios`),
        ]);
        setSedes(sedesRes.data);
        setTiposDoc(tiposRes.data);
        setCiudades(ciudadesRes.data);
        setClientes(clientesRes.data);
        setMedicos(medicosRes.data);
        setExamenes(examenesRes.data);
        setEspecies(especiesRes.data);
        setRazas(razasRes.data);
        setPerfilesExamen(perfilesRes.data);
        setPropietarios(propietariosRes.data || []);
      } catch (error) {
        console.error("Error cargando datos:", error);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const clienteDefault = clientes.find(
      (c) =>
        normalizar(c.clte_codigo) === "PART" &&
        normalizar(c.razon || c.nombre).includes("PARTICULAR"),
    );

    const medicoDefault = medicos.find(
      (m) =>
        normalizar(m.medico_cod) === "MV" &&
        normalizar(m.nombre).includes("MEDICOS VARIOS"),
    );

    const ciudadDefault = ciudades.find((c) => {
      const texto = normalizar(c.ciudad_descrip);
      return texto.includes("CUCUTA") && texto.includes("NORTE DE SANTANDER");
    });

    setForm((prev) => ({
      ...prev,
      clte_codigo: prev.clte_codigo || clienteDefault?.clte_codigo || "",
      medico_cod: prev.medico_cod || medicoDefault?.medico_cod || "",
      ciudad_cod: prev.ciudad_cod || ciudadDefault?.ciudad_cod || "",
    }));
  }, [clientes, medicos, ciudades]);

  useEffect(() => {
    localStorage.setItem(
      "patients_draft_order_active",
      hasDraftOrder ? "1" : "0",
    );
    return () => localStorage.removeItem("patients_draft_order_active");
  }, [hasDraftOrder]);

  useBeforeUnload(
    React.useCallback(
      (event) => {
        if (!hasDraftOrder) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [hasDraftOrder],
    ),
  );

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasDraftOrder) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasDraftOrder]);

  useEffect(() => {
    const handleAttemptNavigate = (event) => {
      if (!hasDraftOrder) return;
      const detail = event.detail || {};
      setPendingNavigation({
        action: detail.action || "navigate",
        to: detail.to || null,
      });
      setShowExitModal(true);
    };

    window.addEventListener("patients:attempt-leave", handleAttemptNavigate);
    return () =>
      window.removeEventListener(
        "patients:attempt-leave",
        handleAttemptNavigate,
      );
  }, [hasDraftOrder]);

  // Auto-load orders for today when edit tab is first opened
  useEffect(() => {
    if (activeTab !== "editar" || ordenes.length > 0) return;
    axios
      .get(`${API_URL}/orders`, {
        params: { startDate: todayStr, endDate: todayStr },
      })
      .then((res) => setOrdenes(res.data || []))
      .catch(() => {});
    // eslint-disable-next-line
  }, [activeTab]);

  // ── Registrar tab: helpers ─────────────────────────────────────────────────
  const addExamenesSinDuplicados = (codigos = []) => {
    setForm((prev) => {
      const actuales = prev.examenes || [];
      const normalizados = codigos.filter(Boolean).map((c) => String(c));
      const nuevosUnicos = normalizados.filter(
        (cod) => !actuales.includes(cod),
      );
      const duplicados = normalizados.length - nuevosUnicos.length;

      if (duplicados > 0) {
        toast.info(`Se omitieron ${duplicados} examen(es) duplicado(s).`);
      }

      return {
        ...prev,
        examenes: [...actuales, ...nuevosUnicos],
      };
    });
  };

  const quitarExamen = (examenCod) => {
    setForm((prev) => ({
      ...prev,
      examenes: (prev.examenes || []).filter((cod) => cod !== examenCod),
    }));
  };

  const handleNuevo = async () => {
    if (!selectedSede) {
      toast.error("Selecciona una sede para continuar.");
      return;
    }

    setLoading(true);
    try {
      const fecha = new Date().toISOString().split("T")[0];
      const res = await axios.post(`${API_URL}/pacientes/consecutivo`, {
        sede_codigo: selectedSede,
        fecha,
      });

      if (res.data?.paciente_cod) {
        setNuevoConsecutivo(res.data.paciente_cod);
        toast.info(`✅ Consecutivo creado: ${res.data.paciente_cod}`);
        setShowForm(true);
      } else {
        toast.warning("⚠️ No se recibió el número de consecutivo.");
        setLoading(false);
      }
    } catch (error) {
      console.error("❌ Error creando consecutivo:", error);
      toast.error("Error al crear el consecutivo.");
      setLoading(false);
    }
  };

  const cargarPacientesPropietario = async (nit) => {
    try {
      const pacRes = await axios.get(
        `${API_URL}/propietarios/pacientes/${nit}`,
      );
      setPacientes(pacRes.data || []);
    } catch (error) {
      setPacientes([]);
    }
  };

  const cargarDatosPropietario = async (nitPropietario) => {
    const res = await axios.get(`${API_URL}/propietarios/${nitPropietario}`);
    if (!res.data) return;

    setForm((prev) => ({ ...prev, ...res.data, examenes: prev.examenes }));
    setOwnerExists(true);
    setShowOwnerForm(false);
    const pacRes = await axios.get(
      `${API_URL}/propietarios/pacientes/${nitPropietario}`,
    );
    const pacientesOwner = pacRes.data || [];
    setPacientes(pacientesOwner);
    setShowPetForm(pacientesOwner.length === 0);
  };

  const buscarPropietario = async () => {
    if (!form.nit) return toast.error("Ingresa el número de documento.");
    try {
      await cargarDatosPropietario(form.nit);
      setSelectedOwner(form.nit);
      toast.success("Propietario encontrado.");
    } catch (error) {
      setOwnerExists(false);
      setPacientes([]);
      setShowOwnerForm(true);
      toast.info("No se encontró propietario. Puedes registrarlo como nuevo.");
    }
  };

  const handleOwnerSelect = async (e) => {
    const ownerNit = e.target.value;
    setSelectedOwner(ownerNit);

    if (!ownerNit) {
      setOwnerExists(false);
      setShowOwnerForm(false);
      setPacientes([]);
      setShowPetForm(false);
      setPacienteSeleccionado("");
      setForm((prev) => ({
        ...prev,
        tipodcto_cod: "",
        nit: "",
        nom1: "",
        ape1: "",
        direccion: "",
        telefono: "",
        celular: "",
        email: "",
      }));
      return;
    }

    if (ownerNit === NUEVO_OPTION) {
      setOwnerExists(false);
      setShowOwnerForm(true);
      setShowPetForm(false);
      setPacientes([]);
      setPacienteSeleccionado("");
      setForm((prev) => ({
        ...prev,
        tipodcto_cod: "",
        nit: "",
        nom1: "",
        ape1: "",
        direccion: "",
        telefono: "",
        celular: "",
        email: "",
      }));
      return;
    }

    try {
      await cargarDatosPropietario(ownerNit);
      setPacienteSeleccionado("");
    } catch (error) {
      toast.error("No se pudo cargar el propietario seleccionado.");
    }
  };

  const handlePacienteSelect = (e) => {
    const value = e.target.value;
    setPacienteSeleccionado(value);

    if (value === NUEVO_OPTION) {
      setShowPetForm(true);
      setForm((prev) => ({ ...prev, consecutivo_especie: null }));
      return;
    }

    const selected = pacientes.find(
      (p) => String(p.consecutivo_especie) === String(value),
    );

    setShowPetForm(false);
    setForm((prev) => ({
      ...prev,
      consecutivo_especie: selected?.consecutivo_especie || null,
    }));
  };

  const registrarPropietario = async () => {
    if (!form.tipodcto_cod || !form.nit || !form.nom1 || !form.ape1) {
      toast.error("Completa tipo doc, documento, nombre y apellido.");
      return;
    }

    const nitDuplicado = propietarios.some(
      (p) => String(p.nit).trim() === String(form.nit).trim(),
    );
    if (nitDuplicado) {
      toast.error(
        "Ya existe un convenio registrado con este número de documento.",
      );
      return;
    }

    try {
      await axios.post(`${API_URL}/propietarios`, form);
      setOwnerExists(true);
      setSelectedOwner(form.nit);
      setShowOwnerForm(false);
      setShowPetForm(true);
      const propietariosRes = await axios.get(`${API_URL}/propietarios`);
      setPropietarios(propietariosRes.data || []);
      toast.success("Propietario creado correctamente.");
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(
          error.response.data?.error ||
            "Ya existe un convenio con este número de documento.",
        );
      } else {
        console.error(error);
        toast.error("No fue posible crear el propietario.");
      }
    }
  };

  const registrarPaciente = async () => {
    if (!ownerExists) {
      toast.error("Primero debes registrar o buscar un propietario válido.");
      return;
    }

    if (!petForm.nombre || !petForm.tipo_especie || !petForm.tipo_raza) {
      toast.error("Completa nombre, especie y raza del paciente.");
      return;
    }

    try {
      const payload = {
        ...petForm,
        tipodcto_cod: form.tipodcto_cod,
        nit: form.nit,
        usuario: user?.usuario,
      };

      const res = await axios.post(
        `${API_URL}/propietarios/pacientes`,
        payload,
      );
      await cargarPacientesPropietario(form.nit);
      setForm((prev) => ({
        ...prev,
        consecutivo_especie: res.data?.consecutivo_especie,
      }));
      setPacienteSeleccionado(String(res.data?.consecutivo_especie || ""));
      setShowPetForm(false);
      setPetForm({
        nombre: "",
        nacio: "",
        genero: "",
        tipo_especie: "",
        tipo_raza: "",
        edad: "",
        med_edad: "",
        caracteristicas: "",
        propietario: "",
      });
      toast.success("Paciente registrado en el propietario.");
    } catch (error) {
      console.error(error);
      toast.error("No fue posible registrar el paciente.");
    }
  };

  const agregarExamenesPorPerfil = async (perfilCod) => {
    if (!perfilCod) return;

    try {
      const res = await axios.get(
        `${API_URL}/util/perfiles_examen/${encodeURIComponent(perfilCod)}`,
      );
      const examenesPerfil = (res.data || [])
        .map((e) => String(e.examen_cod || "").trim())
        .filter(Boolean);

      if (!examenesPerfil.length) {
        toast.warning("El perfil seleccionado no tiene exámenes configurados.");
        return;
      }

      addExamenesSinDuplicados(examenesPerfil);
      toast.success(
        `Se agregaron ${examenesPerfil.length} examen(es) del perfil.`,
      );
    } catch (error) {
      console.error(error);
      toast.error("No se pudieron agregar exámenes del perfil.");
    }
  };

  const handleSubmit = async (e) => {
    setloadingProp(true);
    e.preventDefault();

    if (!ownerExists) {
      toast.error("Debes registrar el propietario antes de crear la orden.");
      setloadingProp(false);
      return;
    }

    if (!form.consecutivo_especie) {
      toast.error("Debes seleccionar o registrar un paciente.");
      setloadingProp(false);
      return;
    }

    if (!form.clte_codigo) {
      toast.error("El campo cliente es obligatorio.");
      setloadingProp(false);
      return;
    }

    if (!form.medico_cod) {
      toast.error("El campo médico es obligatorio.");
      setloadingProp(false);
      return;
    }

    if (!form.examenes?.length) {
      toast.error("Debes agregar al menos un examen para la orden.");
      setloadingProp(false);
      return;
    }

    try {
      const payload = {
        paciente_cod: nuevoConsecutivo,
        sede_codigo: selectedSede,
        tipodcto_cod: form.tipodcto_cod,
        nit: form.nit,
        medico_cod: form.medico_cod,
        clte_codigo: form.clte_codigo,
        consecutivo_especie: form.consecutivo_especie,
        examenes: form.examenes,
        usuario: user?.usuario,
      };

      await axios.post(`${API_URL}/pacientes/orden`, payload);
      toast.success("Orden y exámenes registrados correctamente.");
      setLoading(false);
      setloadingProp(false);

      setForm({
        tipodcto_cod: "",
        nit: "",
        nom1: "",
        ape1: "",
        direccion: "",
        telefono: "",
        celular: "",
        email: "",
        ciudad_cod: "",
        zona: "",
        clte_codigo: "",
        medico_cod: "",
        examenes: [],
      });
      setPacientes([]);
      setOwnerExists(false);
      setSelectedOwner("");
      setShowOwnerForm(false);
      setShowPetForm(false);
      setPacienteSeleccionado("");
      setShowForm(false);
    } catch (error) {
      setloadingProp(false);
      console.error("Error guardando propietario u orden:", error);
      toast.error("Error al guardar la orden o exámenes.");
    }
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const resetDraftOrder = () => {
    setForm({
      tipodcto_cod: "",
      nit: "",
      nom1: "",
      ape1: "",
      direccion: "",
      telefono: "",
      celular: "",
      email: "",
      ciudad_cod: "",
      zona: "",
      clte_codigo: "",
      medico_cod: "",
      examenes: [],
    });
    setPetForm({
      nombre: "",
      nacio: "",
      genero: "",
      tipo_especie: "",
      tipo_raza: "",
      edad: "",
      med_edad: "",
      caracteristicas: "",
      propietario: "",
    });
    setSelectedPerfil("");
    setPacientes([]);
    setOwnerExists(false);
    setSelectedOwner("");
    setShowOwnerForm(false);
    setShowPetForm(false);
    setPacienteSeleccionado("");
    setNuevoConsecutivo(null);
    setShowForm(false);
    setLoading(false);
    setloadingProp(false);
  };

  const cancelarOrdenYSalir = () => {
    resetDraftOrder();
    setShowExitModal(false);

    if (pendingNavigation?.action === "logout") {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.clear();
      window.history.pushState(null, "", "/login");
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else if (pendingNavigation?.to) {
      window.history.pushState(null, "", pendingNavigation.to);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }

    setPendingNavigation(null);
  };

  const handlePetChange = (e) => {
    const { name, value } = e.target;

    if (name === "nacio") {
      const edadCalculada = calcularEdadDesdeFecha(value);
      setPetForm((prev) => ({
        ...prev,
        nacio: value,
        edad: edadCalculada.edad,
        med_edad: edadCalculada.med_edad,
      }));
      return;
    }

    setPetForm((prev) => ({ ...prev, [name]: value }));
  };

  const razasFiltradas = razas.filter(
    (r) => !petForm.tipo_especie || r.codigo_especie === petForm.tipo_especie,
  );
  const editRazasFiltradas = razas.filter(
    (r) => !editPetForm.tipo_especie || r.codigo_especie === editPetForm.tipo_especie,
  );

  const handleEditPropietarioChange = async (e) => {
    const nit = e.target.value;
    setEditForm((prev) => ({ ...prev, propietario_nit: nit }));
    setEditPacienteSeleccionado("");
    setEditShowPetForm(false);
    setEditPacientes([]);
    setEditPetForm({ nombre: "", nacio: "", genero: "", tipo_especie: "", tipo_raza: "", edad: "", med_edad: "", caracteristicas: "", propietario: "" });
    if (!nit) return;
    try {
      const res = await axios.get(`${API_URL}/propietarios/pacientes/${nit}`);
      const pacs = res.data || [];
      setEditPacientes(pacs);
      if (pacs.length === 0) setEditShowPetForm(true);
    } catch {
      setEditPacientes([]);
    }
  };

  const handleEditPacienteSelect = (e) => {
    const value = e.target.value;
    setEditPacienteSeleccionado(value);
    setEditShowPetForm(value === NUEVO_OPTION);
    if (value !== NUEVO_OPTION) {
      setEditPetForm({ nombre: "", nacio: "", genero: "", tipo_especie: "", tipo_raza: "", edad: "", med_edad: "", caracteristicas: "", propietario: "" });
    }
  };

  const handleEditPetChange = (e) => {
    const { name, value } = e.target;
    if (name === "nacio") {
      const edadCalc = calcularEdadDesdeFecha(value);
      setEditPetForm((prev) => ({ ...prev, nacio: value, edad: edadCalc.edad, med_edad: edadCalc.med_edad }));
      return;
    }
    setEditPetForm((prev) => ({ ...prev, [name]: value }));
  };

  const registrarPacienteEdit = async () => {
    if (!editForm.propietario_nit) {
      toast.error("Selecciona un propietario primero.");
      return;
    }
    if (!editPetForm.nombre || !editPetForm.tipo_especie || !editPetForm.tipo_raza) {
      toast.error("Completa nombre, especie y raza del paciente.");
      return;
    }
    try {
      const propietario = propietarios.find((p) => p.nit === editForm.propietario_nit);
      const payload = {
        ...editPetForm,
        tipodcto_cod: propietario?.tipodcto_cod || "",
        nit: editForm.propietario_nit,
        usuario: user?.usuario,
      };
      const res = await axios.post(`${API_URL}/propietarios/pacientes`, payload);
      const pacRes = await axios.get(`${API_URL}/propietarios/pacientes/${editForm.propietario_nit}`);
      setEditPacientes(pacRes.data || []);
      setEditPacienteSeleccionado(String(res.data?.consecutivo_especie || ""));
      setEditShowPetForm(false);
      setEditPetForm({ nombre: "", nacio: "", genero: "", tipo_especie: "", tipo_raza: "", edad: "", med_edad: "", caracteristicas: "", propietario: "" });
      toast.success("Paciente registrado correctamente.");
    } catch {
      toast.error("No fue posible registrar el paciente.");
    }
  };

  // ── Editar tab: helpers ────────────────────────────────────────────────────
  const buscarOrdenes = async () => {
    setLoadingOrdenes(true);
    try {
      const res = await axios.get(`${API_URL}/orders`, {
        params: { startDate: editFechaDesde, endDate: editFechaHasta },
      });
      setOrdenes(res.data || []);
      if (!res.data?.length) {
        toast.info("No se encontraron órdenes en ese rango de fechas.");
      }
    } catch (error) {
      toast.error("Error al buscar órdenes.");
    } finally {
      setLoadingOrdenes(false);
    }
  };

  const handleOrdenSelect = async (e) => {
    const cod = e.target.value;
    setSelectedOrdenCod(cod);
    setEditNuevosExamenes([]);
    setEditSelectedPerfil("");

    if (!cod) {
      setOrdenDetalle(null);
      setEditForm({ medico_cod: "", clte_codigo: "", propietario_nit: "" });
      setEditExamenes([]);
      setEditPacientes([]);
      setEditPacienteSeleccionado("");
      setEditShowPetForm(false);
      return;
    }

    setLoadingOrdenDetalle(true);
    try {
      const res = await axios.get(`${API_URL}/orders/${cod}`);
      setOrdenDetalle(res.data);
      setEditForm({
        medico_cod: res.data.medico_cod || "",
        clte_codigo: res.data.clte_codigo || "",
        propietario_nit: res.data.nit || "",
      });
      setEditExamenes(res.data.examenes || []);
      if (res.data.nit) {
        try {
          const pacRes = await axios.get(`${API_URL}/propietarios/pacientes/${res.data.nit}`);
          setEditPacientes(pacRes.data || []);
          setEditPacienteSeleccionado(String(res.data.consecutivo_especie || ""));
        } catch {
          setEditPacientes([]);
        }
      }
    } catch (error) {
      toast.error("Error al cargar los datos de la orden.");
      setOrdenDetalle(null);
    } finally {
      setLoadingOrdenDetalle(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedOrdenCod) return;
    if (!editForm.clte_codigo || !editForm.medico_cod) {
      toast.error("Cliente y médico son obligatorios.");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API_URL}/orders/${selectedOrdenCod}`, {
        medico_cod: editForm.medico_cod,
        clte_codigo: editForm.clte_codigo,
        nit: editForm.propietario_nit || undefined,
        consecutivo_especie:
          editPacienteSeleccionado && editPacienteSeleccionado !== NUEVO_OPTION
            ? editPacienteSeleccionado
            : undefined,
      });
      toast.success("Orden actualizada correctamente.");
    } catch (error) {
      toast.error("Error al actualizar la orden.");
    } finally {
      setSavingEdit(false);
    }
  };

  const addEditExamenesSinDuplicados = (codigos = []) => {
    setEditNuevosExamenes((prev) => {
      const existentesEnOrden = editExamenes.map((e) => e.examen_cod);
      const normalizados = codigos.filter(Boolean).map((c) => String(c));
      const nuevosUnicos = normalizados.filter(
        (cod) => !prev.includes(cod) && !existentesEnOrden.includes(cod),
      );
      const duplicados = normalizados.length - nuevosUnicos.length;
      if (duplicados > 0) {
        toast.info(`Se omitieron ${duplicados} examen(es) ya en la orden.`);
      }
      return [...prev, ...nuevosUnicos];
    });
  };

  const quitarEditNuevoExamen = (examenCod) => {
    setEditNuevosExamenes((prev) => prev.filter((c) => c !== examenCod));
  };

  const agregarExamenesPorPerfilEdit = async (perfilCod) => {
    if (!perfilCod) return;
    try {
      const res = await axios.get(
        `${API_URL}/util/perfiles_examen/${encodeURIComponent(perfilCod)}`,
      );
      const examenesPerfil = (res.data || [])
        .map((e) => String(e.examen_cod || "").trim())
        .filter(Boolean);
      if (!examenesPerfil.length) {
        toast.warning("El perfil no tiene exámenes configurados.");
        return;
      }
      addEditExamenesSinDuplicados(examenesPerfil);
    } catch (error) {
      toast.error("No se pudieron cargar exámenes del perfil.");
    }
  };

  const handleConfirmarAgregarExamenes = async () => {
    if (!editNuevosExamenes.length) {
      toast.error("Selecciona al menos un examen para agregar.");
      return;
    }

    let agregados = 0;
    let duplicados = 0;

    for (const examen_cod of editNuevosExamenes) {
      try {
        await axios.post(`${API_URL}/orders/${selectedOrdenCod}/exams`, {
          examen_cod,
          usuario: user?.usuario,
        });
        agregados++;
      } catch (error) {
        if (error.response?.status === 409) {
          duplicados++;
        } else {
          toast.error(`Error al agregar examen ${examen_cod}.`);
        }
      }
    }

    if (duplicados > 0) {
      toast.info(`${duplicados} examen(es) ya estaban en la orden.`);
    }
    if (agregados > 0) {
      toast.success(`${agregados} examen(es) agregado(s) correctamente.`);
      setEditNuevosExamenes([]);
      setEditSelectedPerfil("");
      try {
        const res = await axios.get(`${API_URL}/orders/${selectedOrdenCod}`);
        setEditExamenes(res.data.examenes || []);
      } catch {}
    }
  };

  const iniciarEliminarExamen = (examen) => {
    setConfirmDeleteExam({
      examen_cod: examen.examen_cod,
      examen_nombre: examen.examen_nombre,
      tieneResultados: !!(examen.contestado || examen.validado),
    });
  };

  const confirmarEliminarExamen = async () => {
    if (!confirmDeleteExam) return;
    try {
      await axios.delete(
        `${API_URL}/orders/${selectedOrdenCod}/exams/${confirmDeleteExam.examen_cod}?force=true`,
      );
      setEditExamenes((prev) =>
        prev.filter((e) => e.examen_cod !== confirmDeleteExam.examen_cod),
      );
      toast.success("Examen eliminado de la orden.");
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Error al eliminar el examen.",
      );
    } finally {
      setConfirmDeleteExam(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl font-semibold mb-4 text-[#d53b41]">
          Ingreso de ordenes
        </h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b">
          <button
            className={`px-5 py-2 font-medium rounded-t-lg transition-colors ${
              activeTab === "registrar"
                ? "bg-[#d53b41] text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            onClick={() => setActiveTab("registrar")}
          >
            Registrar orden
          </button>
          <button
            className={`px-5 py-2 font-medium rounded-t-lg transition-colors ${
              activeTab === "editar"
                ? "bg-[#d53b41] text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            onClick={() => setActiveTab("editar")}
          >
            Editar orden
          </button>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            TAB: REGISTRAR
            ═══════════════════════════════════════════════════════════════ */}
        {activeTab === "registrar" && (
          <>
            <Card className="mb-6">
              <CardContent className="flex flex-col md:flex-row items-center gap-4">
                <div className="w-full md:w-1/3">
                  <SelectField
                    name="selectedSede"
                    value={selectedSede}
                    disabled={loading}
                    onChange={(e) => setSelectedSede(e.target.value)}
                    placeholder="Seleccione sede"
                    options={sedes}
                    valueKey="sede_codigo"
                    displayFn={(s) => `${s.sede_codigo} - ${s.nombre}`}
                  />
                </div>

                <Button
                  onClick={handleNuevo}
                  variant="default"
                  disabled={loading}
                  className="w-full md:w-auto"
                >
                  <PlusCircle className="h-5 w-5" /> Nuevo
                </Button>
              </CardContent>
            </Card>

            {showForm && (
              <Card>
                <CardContent>
                  <form
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold">
                        Orden
                      </label>
                      <input
                        type="text"
                        name="orden"
                        value={nuevoConsecutivo || ""}
                        readOnly
                        className="p-2 border rounded-lg w-full bg-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold">
                        Cliente
                      </label>
                      <SelectField
                        name="clte_codigo"
                        value={form.clte_codigo}
                        onChange={handleChange}
                        required
                        placeholder="Seleccione"
                        options={clientes}
                        valueKey="clte_codigo"
                        displayFn={(c2) => `${c2.clte_codigo} - ${c2.razon}`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold">
                        Medico
                      </label>
                      <SelectField
                        name="medico_cod"
                        value={form.medico_cod}
                        onChange={handleChange}
                        required
                        placeholder="Seleccione"
                        options={medicos}
                        valueKey="medico_cod"
                        displayFn={(c) => `${c.medico_cod} - ${c.nombre}`}
                      />
                    </div>

                    <div className="col-span-2 border-t pt-4">
                      <h2 className="font-semibold mb-2 text-[#d53b41]">
                        Convenio
                      </h2>
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-semibold">
                        Convenio
                      </label>
                      <SelectField
                        name="propietario"
                        value={selectedOwner}
                        onChange={handleOwnerSelect}
                        placeholder="Seleccione propietario"
                        options={[
                          ...propietarios.map((p) => ({
                            nit: p.nit,
                            label: `${p.nit} - ${p.nom1} ${p.ape1}`,
                          })),
                          { nit: NUEVO_OPTION, label: "NUEVO" },
                        ]}
                        valueKey="nit"
                        displayFn={(p) => p.label}
                      />
                    </div>

                    {showOwnerForm && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold">
                            Tipo documento
                          </label>
                          <SelectField
                            name="tipodcto_cod"
                            value={form.tipodcto_cod}
                            onChange={handleChange}
                            placeholder="Seleccione"
                            options={tiposDoc}
                            valueKey="tipodcto_cod"
                            displayFn={(t) =>
                              `${t.tipodcto_cod} - ${t.descrip}`
                            }
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold">
                            # Documento
                          </label>
                          <div className="flex">
                            <input
                              type="text"
                              name="nit"
                              value={form.nit}
                              onChange={handleChange}
                              className="p-2 border rounded-l-lg w-full"
                            />
                            <button
                              type="button"
                              onClick={buscarPropietario}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 rounded-r-lg"
                            >
                              <Search className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <input
                          type="text"
                          name="nom1"
                          value={form.nom1}
                          onChange={handleChange}
                          placeholder="Nombres"
                          className="p-2 border rounded-lg w-full"
                        />
                        <input
                          type="text"
                          name="ape1"
                          value={form.ape1}
                          onChange={handleChange}
                          placeholder="Apellidos"
                          className="p-2 border rounded-lg w-full"
                        />
                        <input
                          type="text"
                          name="direccion"
                          value={form.direccion}
                          onChange={handleChange}
                          placeholder="Dirección"
                          className="p-2 border rounded-lg w-full"
                        />
                        <input
                          type="text"
                          name="telefono"
                          value={form.telefono}
                          onChange={handleChange}
                          placeholder="Teléfono"
                          className="p-2 border rounded-lg w-full"
                        />
                        <input
                          type="text"
                          name="celular"
                          value={form.celular}
                          onChange={handleChange}
                          placeholder="Celular"
                          className="p-2 border rounded-lg w-full"
                        />
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="Email"
                          className="p-2 border rounded-lg w-full"
                        />

                        <div>
                          <label className="block text-sm font-semibold">
                            Ciudad
                          </label>
                          <SelectField
                            name="ciudad_cod"
                            value={form.ciudad_cod}
                            onChange={handleChange}
                            placeholder="Seleccione"
                            options={ciudades}
                            valueKey="ciudad_cod"
                            displayFn={(c) => c.ciudad_descrip}
                          />
                        </div>

                        <input
                          type="text"
                          name="zona"
                          value={form.zona}
                          onChange={handleChange}
                          placeholder="Zona"
                          className="p-2 border rounded-lg w-full"
                        />

                        <div className="col-span-2">
                          <Button
                            type="button"
                            onClick={registrarPropietario}
                            variant="outline"
                          >
                            Registrar propietario
                          </Button>
                        </div>
                      </>
                    )}

                    <div className="col-span-2 border-t pt-4">
                      <h2 className="font-semibold mb-2 text-[#d53b41]">
                        Paciente
                      </h2>
                    </div>

                    {ownerExists && (
                      <div className="col-span-2">
                        <label className="block text-sm font-semibold">
                          Pacientes registrados
                        </label>
                        <SelectField
                          name="pacienteSeleccionado"
                          value={pacienteSeleccionado}
                          onChange={handlePacienteSelect}
                          placeholder="Seleccione un paciente"
                          options={[
                            ...pacientes.map((p) => ({
                              consecutivo_especie: String(
                                p.consecutivo_especie,
                              ),
                              label: `${p.nombre} — ${p.especie} (${p.raza})${p.propietario ? ` · Propietario: ${p.propietario}` : ""}`,
                            })),
                            {
                              consecutivo_especie: NUEVO_OPTION,
                              label: "NUEVO",
                            },
                          ]}
                          valueKey="consecutivo_especie"
                          displayFn={(p) => p.label}
                        />
                      </div>
                    )}

                    {showPetForm && (
                      <>
                        <input
                          type="text"
                          name="nombre"
                          value={petForm.nombre}
                          onChange={handlePetChange}
                          placeholder="Nombre paciente"
                          className="p-2 border rounded-lg w-full"
                        />
                        <input
                          type="date"
                          name="nacio"
                          value={petForm.nacio}
                          onChange={handlePetChange}
                          className="p-2 border rounded-lg w-full"
                        />

                        <SelectField
                          name="genero"
                          value={petForm.genero}
                          onChange={handlePetChange}
                          placeholder="Sexo"
                          options={[
                            { codigo: "M", detalle: "Macho" },
                            { codigo: "H", detalle: "Hembra" },
                          ]}
                          valueKey="codigo"
                          displayFn={(g) => g.detalle}
                        />

                        <SelectField
                          name="tipo_especie"
                          value={petForm.tipo_especie}
                          onChange={handlePetChange}
                          placeholder="Especie"
                          options={especies}
                          valueKey="codigo"
                          displayFn={(e) => e.detalle}
                        />

                        <SelectField
                          name="tipo_raza"
                          value={petForm.tipo_raza}
                          onChange={handlePetChange}
                          placeholder="Raza"
                          options={razasFiltradas}
                          valueKey="codigo_raza"
                          displayFn={(r) => r.raza}
                        />

                        <input
                          type="number"
                          name="edad"
                          value={petForm.edad}
                          onChange={handlePetChange}
                          placeholder="Edad"
                          readOnly
                          className="p-2 border rounded-lg bg-gray-100 w-full"
                        />

                        <SelectField
                          name="med_edad"
                          value={petForm.med_edad}
                          onChange={handlePetChange}
                          placeholder="Unidad edad"
                          options={[
                            { codigo: "D", detalle: "Días" },
                            { codigo: "M", detalle: "Meses" },
                            { codigo: "A", detalle: "Años" },
                          ]}
                          valueKey="codigo"
                          displayFn={(m) => m.detalle}
                        />

                        <input
                          type="text"
                          name="propietario"
                          value={petForm.propietario}
                          onChange={handlePetChange}
                          placeholder="Propietario"
                          className="p-2 border rounded-lg w-full"
                        />

                        <input
                          type="text"
                          name="caracteristicas"
                          value={petForm.caracteristicas}
                          onChange={handlePetChange}
                          placeholder="Características"
                          className="p-2 border rounded-lg w-full"
                        />

                        <div className="col-span-2">
                          <Button
                            type="button"
                            onClick={registrarPaciente}
                            variant="outline"
                          >
                            Registrar paciente
                          </Button>
                        </div>
                      </>
                    )}

                    <div className="col-span-2 border-t pt-4">
                      <h2 className="font-semibold mb-2 text-[#d53b41]">
                        Examenes
                      </h2>
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-semibold mb-1">
                        Perfil de exámenes
                      </label>
                      <SelectField
                        name="perfil"
                        value={selectedPerfil}
                        onChange={(e) => {
                          const perfilCod = e.target.value;
                          setSelectedPerfil(perfilCod);
                          agregarExamenesPorPerfil(perfilCod);
                        }}
                        placeholder="Seleccione un perfil"
                        options={perfilesExamen}
                        valueKey="perfil_cod"
                        displayFn={(perfil) =>
                          `${perfil.perfil_cod} - ${perfil.detalle}`
                        }
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-semibold mb-1">
                        Exámenes
                      </label>
                      <SelectField
                        isMulti
                        name="examenes"
                        options={examenes}
                        value={form.examenes}
                        valueKey="examen_cod"
                        searchMode="examCodeFirst"
                        displayFn={(ex) => `${ex.examen_cod} - ${ex.nombre}`}
                        placeholder="Buscar y seleccionar exámenes..."
                        onChange={(e) => {
                          const codigos = (e.target.value || []).map((opt) =>
                            String(opt.examen_cod),
                          );

                          const codigosUnicos = Array.from(new Set(codigos));
                          if (codigosUnicos.length !== codigos.length) {
                            toast.info("Se omitieron exámenes duplicados.");
                          }

                          setForm({
                            ...form,
                            examenes: codigosUnicos,
                          });
                        }}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold mb-2">
                        Exámenes agregados a la orden
                      </label>
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="text-left p-2">Código</th>
                              <th className="text-left p-2">Nombre</th>
                              <th className="text-left p-2 w-24">Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {form.examenes?.length ? (
                              form.examenes.map((codigo) => {
                                const examen = examenes.find(
                                  (ex) => ex.examen_cod === codigo,
                                );
                                return (
                                  <tr key={codigo} className="border-t">
                                    <td className="p-2">{codigo}</td>
                                    <td className="p-2">
                                      {examen?.nombre || "Examen"}
                                    </td>
                                    <td className="p-2">
                                      <button
                                        type="button"
                                        onClick={() => quitarExamen(codigo)}
                                        className="text-red-600 hover:text-red-700"
                                        title="Quitar examen"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={3} className="p-3 text-gray-500">
                                  No hay exámenes agregados.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      variant="default"
                      disabled={loadingProp}
                    >
                      Crear orden
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelarOrdenYSalir}
                    >
                      Cancelar orden
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            TAB: EDITAR
            ═══════════════════════════════════════════════════════════════ */}
        {activeTab === "editar" && (
          <>
            {/* Buscador de órdenes */}
            <Card className="mb-4">
              <CardContent className="flex flex-col md:flex-row items-end gap-4">
                <div className="w-full md:w-auto">
                  <label className="block text-sm font-semibold mb-1">
                    Desde
                  </label>
                  <input
                    type="date"
                    value={editFechaDesde}
                    onChange={(e) => setEditFechaDesde(e.target.value)}
                    className="p-2 border rounded-lg w-full"
                  />
                </div>
                <div className="w-full md:w-auto">
                  <label className="block text-sm font-semibold mb-1">
                    Hasta
                  </label>
                  <input
                    type="date"
                    value={editFechaHasta}
                    onChange={(e) => setEditFechaHasta(e.target.value)}
                    className="p-2 border rounded-lg w-full"
                  />
                </div>
                <Button
                  onClick={buscarOrdenes}
                  variant="default"
                  disabled={loadingOrdenes}
                  className="w-full md:w-auto"
                >
                  <Search className="h-4 w-4" />
                  {loadingOrdenes ? "Buscando..." : "Buscar órdenes"}
                </Button>
              </CardContent>
            </Card>

            {/* Selector de orden */}
            <Card className="mb-4">
              <CardContent>
                <label className="block text-sm font-semibold mb-1">
                  Seleccionar orden
                </label>
                <SelectField
                  name="ordenSeleccionada"
                  value={selectedOrdenCod}
                  onChange={handleOrdenSelect}
                  placeholder="Buscar por código, convenio o paciente..."
                  options={ordenes}
                  valueKey="paciente_cod"
                  displayFn={(o) =>
                    `${o.paciente_cod} — ${o.propietario || o.nit || ""}${o.paciente ? ` | ${o.paciente}` : ""}`
                  }
                />
              </CardContent>
            </Card>

            {loadingOrdenDetalle && (
              <p className="text-gray-500 text-sm mb-4">
                Cargando datos de la orden...
              </p>
            )}

            {ordenDetalle && (
              <Card>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Datos de la orden (solo lectura) */}
                  <div>
                    <label className="block text-sm font-semibold">Orden</label>
                    <input
                      type="text"
                      value={ordenDetalle.paciente_cod || ""}
                      readOnly
                      className="p-2 border rounded-lg w-full bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold">Fecha</label>
                    <input
                      type="text"
                      value={ordenDetalle.fecha || ""}
                      readOnly
                      className="p-2 border rounded-lg w-full bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold">Sede</label>
                    <input
                      type="text"
                      value={ordenDetalle.sede_codigo || ""}
                      readOnly
                      className="p-2 border rounded-lg w-full bg-gray-100"
                    />
                  </div>

                  {/* Campos editables */}
                  <div>
                    <label className="block text-sm font-semibold">
                      Cliente
                    </label>
                    <SelectField
                      name="edit_clte_codigo"
                      value={editForm.clte_codigo}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          clte_codigo: e.target.value,
                        }))
                      }
                      placeholder="Seleccione"
                      options={clientes}
                      valueKey="clte_codigo"
                      displayFn={(c) => `${c.clte_codigo} - ${c.razon}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold">
                      Médico
                    </label>
                    <SelectField
                      name="edit_medico_cod"
                      value={editForm.medico_cod}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          medico_cod: e.target.value,
                        }))
                      }
                      placeholder="Seleccione"
                      options={medicos}
                      valueKey="medico_cod"
                      displayFn={(m) => `${m.medico_cod} - ${m.nombre}`}
                    />
                  </div>

                  {/* Convenio (editable) */}
                  <div className="col-span-2 border-t pt-3">
                    <h2 className="font-semibold text-[#d53b41] mb-2">
                      Convenio (Propietario)
                    </h2>
                    <SelectField
                      name="edit_propietario_nit"
                      value={editForm.propietario_nit}
                      onChange={handleEditPropietarioChange}
                      placeholder="Seleccione propietario"
                      options={propietarios}
                      valueKey="nit"
                      displayFn={(p) => `${p.nit} - ${p.nom1} ${p.ape1}`}
                    />
                  </div>

                  {/* Paciente (editable) */}
                  <div className="col-span-2 border-t pt-3">
                    <h2 className="font-semibold text-[#d53b41] mb-2">
                      Paciente
                    </h2>

                    {editForm.propietario_nit ? (
                      <div className="mb-3">
                        <SelectField
                          name="editPacienteSeleccionado"
                          value={editPacienteSeleccionado}
                          onChange={handleEditPacienteSelect}
                          placeholder="Seleccione un paciente"
                          options={[
                            ...editPacientes.map((p) => ({
                              consecutivo_especie: String(p.consecutivo_especie),
                              label: `${p.nombre} — ${p.especie} (${p.raza})`,
                            })),
                            { consecutivo_especie: NUEVO_OPTION, label: "NUEVO" },
                          ]}
                          valueKey="consecutivo_especie"
                          displayFn={(p) => p.label}
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm bg-gray-50 rounded-lg p-3">
                        <div>
                          <span className="font-medium text-gray-500">Nombre: </span>
                          <span>{ordenDetalle.paciente_nombre || "—"}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">Especie: </span>
                          <span>{ordenDetalle.especie || "—"}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">Raza: </span>
                          <span>{ordenDetalle.raza || "—"}</span>
                        </div>
                      </div>
                    )}

                    {editShowPetForm && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 border-t pt-3">
                        <input
                          name="nombre"
                          value={editPetForm.nombre}
                          onChange={handleEditPetChange}
                          placeholder="Nombre paciente"
                          className="p-2 border rounded-lg w-full"
                        />
                        <input
                          type="date"
                          name="nacio"
                          value={editPetForm.nacio}
                          onChange={handleEditPetChange}
                          className="p-2 border rounded-lg w-full"
                        />
                        <SelectField
                          name="genero"
                          value={editPetForm.genero}
                          onChange={handleEditPetChange}
                          placeholder="Sexo"
                          options={[
                            { codigo: "M", detalle: "Macho" },
                            { codigo: "H", detalle: "Hembra" },
                          ]}
                          valueKey="codigo"
                          displayFn={(g) => g.detalle}
                        />
                        <SelectField
                          name="tipo_especie"
                          value={editPetForm.tipo_especie}
                          onChange={handleEditPetChange}
                          placeholder="Especie"
                          options={especies}
                          valueKey="codigo"
                          displayFn={(e) => e.detalle}
                        />
                        <SelectField
                          name="tipo_raza"
                          value={editPetForm.tipo_raza}
                          onChange={handleEditPetChange}
                          placeholder="Raza"
                          options={editRazasFiltradas}
                          valueKey="codigo_raza"
                          displayFn={(r) => r.raza}
                        />
                        <input
                          type="number"
                          name="edad"
                          value={editPetForm.edad}
                          readOnly
                          placeholder="Edad"
                          className="p-2 border rounded-lg bg-gray-100 w-full"
                        />
                        <SelectField
                          name="med_edad"
                          value={editPetForm.med_edad}
                          onChange={handleEditPetChange}
                          placeholder="Unidad edad"
                          options={[
                            { codigo: "D", detalle: "Días" },
                            { codigo: "M", detalle: "Meses" },
                            { codigo: "A", detalle: "Años" },
                          ]}
                          valueKey="codigo"
                          displayFn={(m) => m.detalle}
                        />
                        <input
                          name="propietario"
                          value={editPetForm.propietario}
                          onChange={handleEditPetChange}
                          placeholder="Propietario"
                          className="p-2 border rounded-lg w-full"
                        />
                        <input
                          name="caracteristicas"
                          value={editPetForm.caracteristicas}
                          onChange={handleEditPetChange}
                          placeholder="Características"
                          className="p-2 border rounded-lg w-full"
                        />
                        <div className="col-span-2">
                          <Button type="button" variant="outline" onClick={registrarPacienteEdit}>
                            Registrar paciente
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Exámenes actuales */}
                  <div className="col-span-2 border-t pt-3">
                    <h2 className="font-semibold text-[#d53b41] mb-2">
                      Exámenes de la orden
                    </h2>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2">Código</th>
                            <th className="text-left p-2">Nombre</th>
                            <th className="text-left p-2">Estado</th>
                            <th className="text-left p-2 w-20">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editExamenes.length ? (
                            editExamenes.map((examen) => (
                              <tr key={examen.examen_cod} className="border-t">
                                <td className="p-2">{examen.examen_cod}</td>
                                <td className="p-2">
                                  {examen.examen_nombre || examen.examen_cod}
                                </td>
                                <td className="p-2">
                                  {(examen.contestado || examen.validado) && (
                                    <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                      <AlertTriangle className="h-3 w-3" />
                                      Con resultados
                                    </span>
                                  )}
                                </td>
                                <td className="p-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      iniciarEliminarExamen(examen)
                                    }
                                    className="text-red-600 hover:text-red-700"
                                    title="Eliminar examen"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={4}
                                className="p-3 text-gray-500 text-center"
                              >
                                No hay exámenes en esta orden.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Agregar nuevos exámenes */}
                  <div className="col-span-2 border-t pt-3">
                    <h2 className="font-semibold text-[#d53b41] mb-2">
                      Agregar exámenes
                    </h2>

                    <div className="mb-3">
                      <label className="block text-sm font-semibold mb-1">
                        Perfil de exámenes
                      </label>
                      <SelectField
                        name="editPerfil"
                        value={editSelectedPerfil}
                        onChange={(e) => {
                          const perfilCod = e.target.value;
                          setEditSelectedPerfil(perfilCod);
                          agregarExamenesPorPerfilEdit(perfilCod);
                        }}
                        placeholder="Seleccione un perfil"
                        options={perfilesExamen}
                        valueKey="perfil_cod"
                        displayFn={(p) => `${p.perfil_cod} - ${p.detalle}`}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="block text-sm font-semibold mb-1">
                        Exámenes
                      </label>
                      <SelectField
                        isMulti
                        name="editExamenes"
                        options={examenes}
                        value={editNuevosExamenes}
                        valueKey="examen_cod"
                        searchMode="examCodeFirst"
                        displayFn={(ex) => `${ex.examen_cod} - ${ex.nombre}`}
                        placeholder="Buscar y seleccionar exámenes..."
                        onChange={(e) => {
                          const codigos = (e.target.value || []).map((opt) =>
                            String(opt.examen_cod),
                          );
                          const existentesEnOrden = editExamenes.map(
                            (ex) => ex.examen_cod,
                          );
                          const filtrados = codigos.filter(
                            (c) => !existentesEnOrden.includes(c),
                          );
                          const omitidos = codigos.length - filtrados.length;
                          if (omitidos > 0) {
                            toast.info(
                              `Se omitieron ${omitidos} examen(es) ya en la orden.`,
                            );
                          }
                          setEditNuevosExamenes(Array.from(new Set(filtrados)));
                        }}
                      />
                    </div>

                    {editNuevosExamenes.length > 0 && (
                      <div className="overflow-x-auto border rounded-lg mb-3">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-2">Código</th>
                              <th className="text-left p-2">Nombre</th>
                              <th className="text-left p-2 w-20">Quitar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {editNuevosExamenes.map((cod) => {
                              const ex = examenes.find(
                                (e) => e.examen_cod === cod,
                              );
                              return (
                                <tr key={cod} className="border-t">
                                  <td className="p-2">{cod}</td>
                                  <td className="p-2">
                                    {ex?.nombre || "Examen"}
                                  </td>
                                  <td className="p-2">
                                    <button
                                      type="button"
                                      onClick={() => quitarEditNuevoExamen(cod)}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleConfirmarAgregarExamenes}
                      disabled={!editNuevosExamenes.length}
                    >
                      <PlusCircle className="h-4 w-4" /> Agregar exámenes a la
                      orden
                    </Button>
                  </div>

                  {/* Guardar cambios */}
                  <div className="col-span-2 border-t pt-4 flex justify-end">
                    <Button
                      type="button"
                      variant="default"
                      onClick={handleEditSubmit}
                      disabled={savingEdit}
                    >
                      <Save className="h-4 w-4" />
                      {savingEdit ? "Guardando..." : "Guardar cambios"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* ── Modal: confirmar salir con orden en borrador ─────────────────── */}
      {showExitModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-5 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              ¿Seguro que deseas salir y cancelar esta orden?
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">
              Si sales ahora, el consecutivo <strong>{nuevoConsecutivo}</strong>{" "}
              se perderá y no podrá volver a usarse. Esta acción cancelará el
              proceso actual de creación de la orden.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowExitModal(false)}
              >
                Continuar editando
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={cancelarOrdenYSalir}
              >
                Salir y cancelar orden
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: confirmar eliminar examen ─────────────────────────────── */}
      {confirmDeleteExam && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-5 sm:p-6">
            {confirmDeleteExam.tieneResultados ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Examen con resultados registrados
                  </h2>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  El examen{" "}
                  <strong>
                    {confirmDeleteExam.examen_nombre ||
                      confirmDeleteExam.examen_cod}
                  </strong>{" "}
                  ya tiene resultados ingresados. Si lo eliminas, los resultados
                  también se perderán.
                </p>
                <p className="text-sm text-amber-700 font-medium mb-6">
                  ¿Deseas eliminar el examen y sus resultados de todas formas?
                </p>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  Eliminar examen
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  ¿Confirmas que deseas eliminar el examen{" "}
                  <strong>
                    {confirmDeleteExam.examen_nombre ||
                      confirmDeleteExam.examen_cod}
                  </strong>{" "}
                  de esta orden?
                </p>
              </>
            )}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDeleteExam(null)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={confirmarEliminarExamen}
              >
                Sí, eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
